import type { BlogPost } from "@/types/content";

// Spine, back pain and neck pain articles (posts 1-15).
// Article images are managed centrally in src/data/blog/images.ts (keyed by slug).
export const spinePosts: BlogPost[] = [
  {
    id: 1,
    slug: "what-causes-back-pain-and-when-to-see-a-spine-specialist",
    title: "What Causes Back Pain and When Should You See a Spine Specialist?",
    category: "Back Pain",
    excerpt:
      "Back pain is extremely common, but some causes and warning signs need a specialist opinion. Here is what to look for.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Calm%20modern%20medical%20consultation%20room%20with%20a%20spine%20model%20and%20X-ray%20on%20a%20clean%20desk%2C%20soft%20natural%20light%2C%20professional%20healthcare%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-01&orientation=landscape&nocache=true",
    imageAlt: "Spine specialist consultation room for back pain assessment",
    metaTitle: "What Causes Back Pain? When to See a Spine Specialist | Trichy",
    metaDescription:
      "Understand the common causes of back pain and the warning signs that mean it is time to see a spine specialist. Patient-friendly guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-05",
    updatedDate: "2026-06-12",
    readTime: "5 min read",
    tags: ["Back Pain", "Spine Health", "Spine Specialist"],
    content: [
      {
        heading: "Back pain is common — but not always simple",
        paragraphs: [
          "Almost everyone experiences back pain at some point. In most cases it settles within a few weeks with simple measures. However, persistent or severe pain can point to a disc, joint or nerve problem that deserves a specialist opinion.",
          "Knowing the difference between ordinary strain and something that needs attention helps you decide when to seek care.",
        ],
      },
      {
        heading: "Common causes of back pain",
        bullets: [
          "Muscle strain from lifting, bending or sudden movement",
          "Poor posture and long hours of sitting",
          "Disc problems such as a prolapsed disc pressing on a nerve",
          "Arthritis and age-related wear of the spinal joints",
          "Spinal stenosis, where the spinal canal narrows",
          "Injury or a fall",
        ],
      },
      {
        heading: "Signs that you should see a spine specialist",
        paragraphs: [
          "Consider a specialist opinion if pain lasts more than a few weeks, keeps coming back, or begins to limit your work and daily activities.",
        ],
        bullets: [
          "Pain that travels into the arm or leg",
          "Numbness, tingling or weakness in a limb",
          "Difficulty walking or standing upright",
          "Loss of bladder or bowel control, or numbness around the groin — seek urgent care",
        ],
      },
      {
        heading: "How a specialist assesses back pain",
        paragraphs: [
          "A spine specialist takes a detailed history, examines your movement, strength and nerve function, and arranges imaging only when it will change the treatment plan. The aim is to find the cause and choose the least invasive option that works for you.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long should back pain last before I see a doctor?",
        a: "If it lasts more than a few weeks, keeps returning, or is accompanied by numbness or weakness, it is worth a specialist assessment. Red-flag symptoms such as loss of bladder or bowel control need urgent attention.",
      },
      {
        q: "Do I always need an MRI for back pain?",
        a: "No. Imaging is arranged when it is likely to change the treatment plan. Many episodes of back pain can be assessed clinically and managed without a scan.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain", "disc-problems"],
  },
  {
    id: 2,
    slug: "common-causes-of-lower-back-pain",
    title: "Common Causes of Lower Back Pain",
    category: "Back Pain",
    excerpt: "Lower back pain has many causes, from muscle strain to disc and joint problems. Here is a clear overview.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20lumbar%20spine%20and%20lower%20back%20vertebrae%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-02&orientation=landscape&nocache=true",
    imageAlt: "Person experiencing lower back pain",
    metaTitle: "Common Causes of Lower Back Pain | Ortho Spine Care",
    metaDescription:
      "A patient-friendly guide to the most common causes of lower back pain, how it is assessed and when to seek specialist care from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-12",
    updatedDate: "2026-05-28",
    readTime: "5 min read",
    tags: ["Back Pain", "Lower Back", "Spine Health"],
    content: [
      {
        heading: "Why the lower back is so vulnerable",
        paragraphs: [
          "The lower back carries much of the body weight and does most of the bending and lifting. This makes it especially prone to strain and to the gradual wear that comes with age.",
        ],
      },
      {
        heading: "The most common causes",
        bullets: [
          "Muscle and ligament strain after lifting or a sudden movement",
          "Disc problems, including a bulging or prolapsed disc",
          "Facet joint arthritis — wear in the small joints of the spine",
          "Spinal stenosis, a narrowing of the spinal canal",
          "Spondylolisthesis, where one vertebra slips over another",
          "Poor posture and long periods of sitting",
        ],
      },
      {
        heading: "When lower back pain needs specialist care",
        paragraphs: [
          "Most lower back pain improves with activity modification, simple medication and a graded return to movement. See a specialist if the pain is severe, lasts beyond a few weeks, or comes with leg pain, numbness or weakness.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is lower back pain usually serious?",
        a: "Most cases are not serious and settle with time. Pain that travels into the leg, causes weakness, or follows a fall deserves timely assessment.",
      },
      {
        q: "Should I rest completely when my back hurts?",
        a: "Prolonged bed rest is usually not recommended. Gentle activity within comfort limits is generally better than staying still for long periods.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain"],
  },
  {
    id: 3,
    slug: "back-pain-symptoms-and-treatment-options",
    title: "Back Pain: Common Symptoms and Treatment Options",
    category: "Back Pain",
    excerpt: "What back pain can feel like, and the range of treatment options from simple measures to specialist care.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Anatomical%20spine%20model%20beside%20a%20neat%20stack%20of%20physiotherapy%20exercise%20bands%20on%20a%20clean%20white%20desk%20in%20a%20bright%20clinic%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-03&orientation=landscape&nocache=true",
    imageAlt: "Gentle stretching as part of back pain treatment",
    metaTitle: "Back Pain Symptoms and Treatment Options | Ortho Spine Care",
    metaDescription:
      "Learn about the common symptoms of back pain and the treatment options available, from activity changes and physiotherapy to specialist care, at Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-19",
    updatedDate: "2026-05-20",
    readTime: "6 min read",
    tags: ["Back Pain", "Treatment", "Spine Health"],
    content: [
      {
        heading: "What back pain can feel like",
        bullets: [
          "A dull ache that worsens through the day",
          "Sharp pain with bending, lifting or twisting",
          "Stiffness in the morning or after sitting",
          "Pain that travels into the hip, buttock or leg",
          "Numbness, tingling or weakness in the leg",
        ],
      },
      {
        heading: "Treatment options, from simple to specialist",
        paragraphs: [
          "Treatment is matched to the cause and severity of your pain. Non-surgical options are considered first wherever they are appropriate.",
        ],
        bullets: [
          "Activity modification and a graded return to movement",
          "Physiotherapy to strengthen the supporting muscles",
          "Medication for pain and inflammation during flare-ups",
          "Injections in selected cases",
          "Surgery when there is severe or progressive nerve involvement",
        ],
      },
      {
        heading: "Getting the right plan for you",
        paragraphs: [
          "Because back pain has many causes, the right treatment depends on an accurate diagnosis. A specialist assessment helps identify the cause so treatment can be focused and effective.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do all back pain treatments involve surgery?",
        a: "No. Most people are managed without surgery using activity changes, physiotherapy and medication. Surgery is considered only when it offers a clear benefit.",
      },
      {
        q: "How long does it take to recover from back pain?",
        a: "Many episodes improve within a few weeks, though this varies. Recovery is usually faster with a graded return to activity rather than prolonged rest.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain"],
  },
  {
    id: 4,
    slug: "when-is-back-pain-a-sign-of-a-spine-problem",
    title: "When Is Back Pain a Sign of a Spine Problem?",
    category: "Back Pain",
    excerpt: "Some back pain is simple strain; some points to a spine condition. These clues help tell the difference.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20standing%20spine%20X-ray%20film%20displayed%20on%20a%20modern%20illuminated%20viewing%20screen%20in%20a%20clean%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-04&orientation=landscape&nocache=true",
    imageAlt: "Doctor reviewing a spine X-ray for a back pain diagnosis",
    metaTitle: "When Is Back Pain a Spine Problem? Warning Signs | Ortho Spine Care",
    metaDescription:
      "Not all back pain is the same. Learn the clues that suggest a spine problem and when to seek specialist assessment at Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-26",
    updatedDate: "2026-06-02",
    readTime: "5 min read",
    tags: ["Back Pain", "Spine Health", "Diagnosis"],
    content: [
      {
        heading: "Simple strain versus a spine condition",
        paragraphs: [
          "Simple muscle strain usually improves within days to weeks and is eased by rest and gentle movement. Pain that behaves differently may point to a disc, joint or nerve problem.",
        ],
      },
      {
        heading: "Clues that suggest a spine problem",
        bullets: [
          "Pain that travels down the leg, often below the knee",
          "Numbness, pins and needles or weakness in the leg",
          "Pain that worsens with coughing or sneezing",
          "Stiffness and pain that keeps you awake at night",
          "Difficulty standing straight or walking any distance",
        ],
      },
      {
        heading: "Why the pattern matters",
        paragraphs: [
          "The way pain behaves — where it travels, what makes it better or worse — often tells a specialist more than the pain itself. This pattern, together with examination, guides which tests, if any, are needed.",
        ],
      },
      {
        heading: "When to seek help",
        paragraphs: [
          "If any of the clues above sound familiar, or if pain is persistent, a specialist assessment can confirm the cause and set out the right treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can a spine problem cause pain only in the leg?",
        a: "Yes. A nerve in the spine that is irritated can cause pain, numbness or weakness that is felt mostly in the leg, even when the back itself does not hurt much.",
      },
      {
        q: "Are these signs always serious?",
        a: "Not always, but they are worth assessing. A specialist can identify the cause and reassure you or recommend treatment as needed.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain", "disc-problems"],
  },
  {
    id: 5,
    slug: "understanding-slipped-disc-symptoms-causes-and-treatment",
    title: "Understanding Slipped Disc: Symptoms, Causes and Treatment",
    category: "Spine Health",
    excerpt: "What a slipped disc really means, how it causes pain, and the treatment options that are available.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Anatomical%20spine%20model%20on%20a%20clean%20white%20surface%20in%20a%20bright%20minimal%20medical%20setting%2C%20soft%20daylight%2C%20premium%20health%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-05&orientation=landscape&nocache=true",
    imageAlt: "Anatomical model illustrating a slipped disc in the spine",
    metaTitle: "Slipped Disc: Symptoms, Causes and Treatment | Ortho Spine Care",
    metaDescription:
      "A patient-friendly guide to slipped disc (disc prolapse): what it is, why it causes pain, and the treatment options available at Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-02",
    updatedDate: "2026-06-15",
    readTime: "6 min read",
    tags: ["Disc Problems", "Spine Health", "Back Pain"],
    content: [
      {
        heading: "What a slipped disc actually is",
        paragraphs: [
          "The discs are cushions between the bones of the spine. With age and repeated strain, the softer inner part of a disc can push outwards and press on a nearby nerve. This is commonly called a slipped disc, or disc prolapse.",
        ],
      },
      {
        heading: "Symptoms to look out for",
        bullets: [
          "Sharp or shooting pain in the arm or leg",
          "Numbness or tingling along the affected limb",
          "Weakness in the arm or leg",
          "Pain that worsens with coughing, sneezing or sitting",
        ],
      },
      {
        heading: "Common causes",
        bullets: [
          "Age-related wear of the disc",
          "Repeated heavy lifting or bending",
          "A sudden twisting injury",
        ],
      },
      {
        heading: "Treatment options",
        paragraphs: [
          "Many disc prolapses improve over weeks to months without surgery. Treatment often begins with rest, appropriate medication and a graded physiotherapy programme. Surgery is considered when pain is severe and persistent, or when there is progressive weakness.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can a slipped disc heal without surgery?",
        a: "Yes, many do. A large proportion of disc prolapses improve with non-surgical treatment over weeks to months.",
      },
      {
        q: "What is the best position to lie in with a slipped disc?",
        a: "Comfort varies between people. Many find lying on the side with a pillow between the knees, or on the back with a pillow under the knees, eases symptoms. Avoid prolonged bed rest.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["disc-problems", "back-pain"],
  },
  {
    id: 6,
    slug: "how-poor-posture-can-affect-your-spine",
    title: "How Poor Posture Can Affect Your Spine",
    category: "Spine Health",
    excerpt: "Posture does not just affect how you look — over time it can influence how your spine feels.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Minimal%20ergonomic%20workstation%20setup%20with%20an%20upright%20chair%2C%20a%20monitor%20at%20eye%20level%20and%20a%20supportive%20lumbar%20cushion%20and%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-06&orientation=landscape&nocache=true",
    imageAlt: "Person sitting with good upright posture at a desk",
    metaTitle: "How Poor Posture Affects Your Spine | Ortho Spine Care",
    metaDescription:
      "Learn how sustained poor posture affects the spine and neck, and simple habits that help protect your back. Patient-friendly guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-09",
    updatedDate: "2026-05-30",
    readTime: "5 min read",
    tags: ["Posture", "Spine Health", "Neck Pain"],
    content: [
      {
        heading: "Posture is about habits, not perfection",
        paragraphs: [
          "The spine is designed to keep the head balanced over the body. When we hold one position for a long time — especially a slumped one — the muscles and joints work harder than they need to.",
        ],
      },
      {
        heading: "What sustained poor posture can cause",
        bullets: [
          "Neck and upper back stiffness",
          "Tension headaches arising from the neck",
          "Aching between the shoulder blades",
          "Lower back discomfort after long sitting",
        ],
      },
      {
        heading: "Simple habits that help",
        bullets: [
          "Change position every 30 to 40 minutes",
          "Keep screens at roughly eye level",
          "Support the lower back when sitting for long periods",
          "Take short walking breaks during the day",
          "Strengthen the muscles that support the spine",
        ],
      },
      {
        heading: "When posture-related pain needs attention",
        paragraphs: [
          "Posture-related aches usually ease with better habits. If pain persists, travels into a limb, or comes with numbness or weakness, a specialist assessment is worthwhile.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is bad posture always the cause of back pain?",
        a: "Posture is one of several factors. Disc problems, arthritis and injuries can also cause pain, which is why a specialist looks at the whole picture.",
      },
      {
        q: "Can posture be improved as an adult?",
        a: "Yes. Improving workstation setup, taking breaks and strengthening supporting muscles can improve posture and comfort at any age.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["neck-pain", "back-pain"],
  },
  {
    id: 7,
    slug: "neck-pain-common-causes-and-treatment-options",
    title: "Neck Pain: Common Causes and Treatment Options",
    category: "Neck Pain",
    excerpt: "Neck pain is common and often settles — but some patterns need a closer look.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20cervical%20spine%20and%20neck%20vertebrae%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-07&orientation=landscape&nocache=true",
    imageAlt: "Person holding the back of the neck due to neck pain",
    metaTitle: "Neck Pain: Causes and Treatment Options | Ortho Spine Care",
    metaDescription:
      "Understand the common causes of neck pain and the treatment options available, including when to seek a spine specialist, at Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-16",
    updatedDate: "2026-06-05",
    readTime: "5 min read",
    tags: ["Neck Pain", "Spine Health", "Treatment"],
    content: [
      {
        heading: "Common causes of neck pain",
        bullets: [
          "Poor posture and long hours at a screen",
          "Muscle strain from an awkward position or sleep posture",
          "Age-related wear of the cervical joints",
          "Cervical disc problems pressing on a nerve",
          "Injury, such as whiplash",
        ],
      },
      {
        heading: "When neck pain travels into the arm",
        paragraphs: [
          "If the pain spreads into the arm or hand, or comes with numbness or weakness, a nerve in the neck may be involved. This pattern benefits from a specialist assessment.",
        ],
      },
      {
        heading: "Treatment options",
        bullets: [
          "Posture correction and physiotherapy",
          "Pain relief medication during flare-ups",
          "Injections in selected cases",
          "Surgery for significant nerve compression",
        ],
      },
    ],
    faqs: [
      {
        q: "Is neck pain usually serious?",
        a: "Most neck pain is not serious and improves with simple measures. Pain that travels into the arm, causes weakness, or follows an injury deserves timely evaluation.",
      },
      {
        q: "Should I use a neck collar?",
        a: "Collars are only helpful in some situations and for limited periods. Prolonged use can weaken the neck muscles, so use one only on the advice of a clinician.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["neck-pain", "disc-problems"],
  },
  {
    id: 8,
    slug: "cervical-spine-problems-symptoms-you-should-know",
    title: "Cervical Spine Problems: Symptoms You Should Know",
    category: "Neck Pain",
    excerpt: "The cervical spine supports and protects the neck. Here are the symptoms that suggest something is wrong.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=An%20anatomical%20cervical%20spine%20model%20beside%20a%20printed%20scan%20on%20a%20clean%20white%20desk%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-08&orientation=landscape&nocache=true",
    imageAlt: "Doctor assessing cervical spine movement in a patient",
    metaTitle: "Cervical Spine Problems: Symptoms to Watch | Ortho Spine Care",
    metaDescription:
      "Learn the symptoms of cervical spine problems, from neck stiffness to arm pain, and when to see a spine specialist. Guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-23",
    updatedDate: "2026-05-22",
    readTime: "5 min read",
    tags: ["Neck Pain", "Cervical Spine", "Spine Health"],
    content: [
      {
        heading: "What the cervical spine does",
        paragraphs: [
          "The cervical spine is the section of the spine in the neck. It supports the head, allows a wide range of movement, and protects the nerves that supply the arms and hands.",
        ],
      },
      {
        heading: "Symptoms that may point to a cervical problem",
        bullets: [
          "Persistent neck pain and stiffness",
          "Pain, numbness or tingling in the arm or hand",
          "Weakness or clumsiness in the hand",
          "Headaches arising from the neck",
          "A grinding or catching feeling when turning the head",
        ],
      },
      {
        heading: "How cervical problems are assessed",
        paragraphs: [
          "A specialist examines neck movement, tests the nerves in the arms, and arranges imaging where needed. The results guide treatment, which often begins with non-surgical care.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can cervical spine problems affect the hands?",
        a: "Yes. Nerves from the cervical spine supply the arms and hands. Compression can cause numbness, tingling or weakness in the hand and fingers.",
      },
      {
        q: "When should cervical symptoms be assessed urgently?",
        a: "Seek urgent assessment for sudden severe weakness, difficulty walking, or loss of bladder or bowel control. These can indicate a condition that needs prompt care.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["neck-pain", "disc-problems"],
  },
  {
    id: 9,
    slug: "sciatica-causes-symptoms-and-treatment-options",
    title: "Sciatica: Causes, Symptoms and Treatment Options",
    category: "Spine Health",
    excerpt: "Sciatica is a symptom, not a diagnosis. Understanding the cause is the key to treating it well.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20medical%20illustration%20of%20the%20sciatic%20nerve%20pathway%20running%20from%20the%20lower%20spine%20through%20the%20hip%20and%20down%20the%20leg%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-09&orientation=landscape&nocache=true",
    imageAlt: "Person resting to ease sciatica leg pain",
    metaTitle: "Sciatica: Causes, Symptoms and Treatment | Ortho Spine Care",
    metaDescription:
      "A patient-friendly guide to sciatica: what causes the shooting leg pain, common symptoms and treatment options available at Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-02",
    updatedDate: "2026-06-08",
    readTime: "6 min read",
    tags: ["Sciatica", "Back Pain", "Disc Problems"],
    content: [
      {
        heading: "What sciatica means",
        paragraphs: [
          "Sciatica describes pain that travels along the path of the sciatic nerve, which runs from the lower back through the buttock and down the leg. It is a symptom rather than a diagnosis, and the cause is usually found in the spine.",
        ],
      },
      {
        heading: "Symptoms",
        bullets: [
          "Shooting pain from the lower back into the leg",
          "Numbness or tingling along the leg or foot",
          "Weakness in the leg",
          "Pain that worsens with sitting, coughing or sneezing",
        ],
      },
      {
        heading: "Common causes",
        bullets: [
          "A prolapsed disc pressing on a nerve root",
          "Spinal stenosis narrowing the space around the nerves",
          "Spondylolisthesis, where a vertebra slips forward",
        ],
      },
      {
        heading: "Treatment options",
        paragraphs: [
          "Many people with sciatica improve with non-surgical treatment such as medication, guided physiotherapy and activity modification. When pain is severe or there is progressive weakness, surgery may be considered.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does sciatica go away on its own?",
        a: "Often it improves over weeks to months with non-surgical treatment. Persistent or worsening symptoms should be assessed.",
      },
      {
        q: "Is walking good for sciatica?",
        a: "Gentle walking helps many people, but comfort varies. Stop any activity that sharply increases leg pain and follow advice from your clinician.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["disc-problems", "back-pain", "spinal-stenosis"],
  },
  {
    id: 10,
    slug: "how-to-maintain-a-healthy-spine",
    title: "How to Maintain a Healthy Spine",
    category: "Spine Health",
    excerpt: "Small, consistent habits do more for your spine than any single fix. Here are the ones that matter most.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20a%20healthy%20human%20spine%20presented%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-10&orientation=landscape&nocache=true",
    imageAlt: "Walking outdoors as part of a healthy spine routine",
    metaTitle: "How to Maintain a Healthy Spine | Ortho Spine Care",
    metaDescription:
      "Simple habits that support long-term spine health, including movement, posture and core strength. Practical guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-09",
    updatedDate: "2026-06-18",
    readTime: "5 min read",
    tags: ["Spine Health", "Prevention", "Wellness"],
    content: [
      {
        heading: "Movement is medicine for the spine",
        paragraphs: [
          "The spine is built to move. Regular, gentle activity keeps the discs, joints and muscles healthy and is one of the most reliable ways to protect your back.",
        ],
      },
      {
        heading: "Habits that help",
        bullets: [
          "Stay active most days, even with short walks",
          "Build core and back strength gradually",
          "Break up long periods of sitting",
          "Set up your workstation so the screen is near eye level",
          "Lift with the legs, keeping loads close to the body",
        ],
      },
      {
        heading: "Habits that tend to hurt",
        bullets: [
          "Prolonged bed rest when the back is sore",
          "Sudden spikes in activity after weeks of inactivity",
          "Smoking, which affects disc health",
          "Ignoring persistent or worsening symptoms",
        ],
      },
      {
        heading: "When to check in with a specialist",
        paragraphs: [
          "A healthy routine prevents many problems, but if pain persists, radiates into a limb, or comes with numbness or weakness, a specialist assessment can pinpoint the cause.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the single best exercise for the spine?",
        a: "There is no single best exercise. Consistent, varied movement that you can maintain, along with strength work, is more helpful than any one exercise.",
      },
      {
        q: "Does sitting damage the spine?",
        a: "Sitting itself is not harmful, but staying in one position for long periods can contribute to discomfort. Regular movement breaks make a real difference.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain"],
  },
  {
    id: 11,
    slug: "spine-health-tips-for-people-who-sit-for-long-hours",
    title: "Spine Health Tips for People Who Sit for Long Hours",
    category: "Spine Health",
    excerpt: "If most of your day is spent seated, a few adjustments can make a big difference to your back and neck.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Tidy%20minimal%20home%20office%20with%20an%20ergonomic%20chair%20and%20laptop%20at%20eye%20level%2C%20bright%20natural%20light%2C%20clean%20lifestyle%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-11&orientation=landscape&nocache=true",
    imageAlt: "Ergonomic home office setup to support spine health",
    metaTitle: "Spine Health Tips for Desk Workers | Ortho Spine Care",
    metaDescription:
      "Practical spine health tips for people who sit for long hours, from workstation setup to movement breaks. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-16",
    updatedDate: "2026-06-20",
    readTime: "5 min read",
    tags: ["Spine Health", "Posture", "Desk Work"],
    content: [
      {
        heading: "Why long sitting can be hard on the spine",
        paragraphs: [
          "Sitting for hours loads the discs and strains the muscles that hold you upright. The problem is rarely sitting itself, but sitting still for too long.",
        ],
      },
      {
        heading: "Set up your space well",
        bullets: [
          "Keep the top of the screen near eye level",
          "Support the lower back so it keeps its natural curve",
          "Keep feet flat and knees roughly level with the hips",
          "Place the keyboard so the elbows rest near 90 degrees",
        ],
      },
      {
        heading: "Build movement into your day",
        bullets: [
          "Stand and move every 30 to 40 minutes",
          "Take a short walk during breaks",
          "Try gentle neck and shoulder stretches",
          "Alternate between sitting and standing where possible",
        ],
      },
    ],
    faqs: [
      {
        q: "Are standing desks better than sitting?",
        a: "Standing desks can help when used to alternate positions, but standing still for hours is not a cure. The key is changing position regularly.",
      },
      {
        q: "How often should I take a break from sitting?",
        a: "A short break every 30 to 40 minutes is a practical target. Even one or two minutes of movement helps.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["neck-pain", "back-pain"],
  },
  {
    id: 12,
    slug: "when-should-you-consult-a-spine-specialist",
    title: "When Should You Consult a Spine Specialist?",
    category: "Spine Health",
    excerpt: "Most back and neck pain settles by itself. Here are the signs that mean it is time for a specialist opinion.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20clean%20modern%20consultation%20desk%20with%20a%20spine%20model%2C%20an%20X-ray%20film%20and%20a%20notepad%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-12&orientation=landscape&nocache=true",
    imageAlt: "Patient consulting a spine specialist in a clinic",
    metaTitle: "When to Consult a Spine Specialist | Ortho Spine Care",
    metaDescription:
      "Learn when a spine specialist opinion is worthwhile, and which symptoms need urgent attention. Clear guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-23",
    updatedDate: "2026-06-25",
    readTime: "4 min read",
    tags: ["Spine Specialist", "Spine Health", "Patient Education"],
    content: [
      {
        heading: "Reasons to see a spine specialist",
        bullets: [
          "Pain lasting more than a few weeks despite rest and medication",
          "Pain that keeps returning or steadily worsens",
          "Pain travelling into an arm or leg",
          "Numbness, tingling or weakness in a limb",
          "Difficulty with balance or walking",
        ],
      },
      {
        heading: "Symptoms that need urgent care",
        bullets: [
          "Loss of bladder or bowel control",
          "Progressive weakness in the legs",
          "Numbness around the groin or inner thighs",
          "Severe pain following a major injury or fall",
        ],
      },
      {
        heading: "What happens at your first visit",
        paragraphs: [
          "Your specialist will take a full history, examine you and, where needed, arrange imaging. The goal is a clear diagnosis and a treatment plan that starts with the least invasive option that fits your situation.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need a referral to see a spine specialist?",
        a: "In most cases you can request an appointment directly. Bringing any previous reports and scans helps your consultation.",
      },
      {
        q: "How long does a first consultation take?",
        a: "A first consultation usually involves a detailed history and examination. The time varies depending on your condition and what assessment is needed.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain", "neck-pain", "disc-problems"],
  },
  {
    id: 13,
    slug: "common-spine-conditions-explained",
    title: "Common Spine Conditions Explained",
    category: "Spine Health",
    excerpt: "A plain-language tour of the spine conditions that most often bring people to a specialist.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Row%20of%20anatomical%20spine%20models%20on%20a%20clean%20white%20table%20in%20a%20minimal%20medical%20study%2C%20soft%20daylight%2C%20premium%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-13&orientation=landscape&nocache=true",
    imageAlt: "Anatomical spine models illustrating common spine conditions",
    metaTitle: "Common Spine Conditions Explained | Ortho Spine Care",
    metaDescription:
      "A patient-friendly overview of common spine conditions including disc prolapse, spinal stenosis, spondylolisthesis and spine infections, from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-30",
    updatedDate: "2026-06-28",
    readTime: "6 min read",
    tags: ["Spine Health", "Conditions", "Patient Education"],
    content: [
      {
        heading: "Disc prolapse",
        paragraphs: [
          "The soft inner part of a disc pushes out and presses on a nerve, causing pain, numbness or weakness that may travel into an arm or leg. Many cases improve without surgery.",
        ],
      },
      {
        heading: "Spinal stenosis",
        paragraphs: [
          "The space around the spinal cord or nerves narrows with age, causing heaviness or cramping in the legs when walking that eases with rest. Treatment ranges from physiotherapy to decompression surgery in selected cases.",
        ],
      },
      {
        heading: "Spondylolisthesis",
        paragraphs: [
          "One vertebra slips forward over the one below, which can cause back pain and nerve symptoms. Treatment depends on the degree of slip and the symptoms it causes.",
        ],
      },
      {
        heading: "Other conditions we see",
        bullets: [
          "Spine trauma and fractures",
          "Spine infections",
          "Spine deformities",
          "Spine tumours",
          "Cases needing revision after previous spine surgery",
        ],
      },
    ],
    faqs: [
      {
        q: "Do all spine conditions get worse with age?",
        a: "Not necessarily. Some are stable and manageable with non-surgical care, while others may progress. Regular assessment helps guide the right approach.",
      },
      {
        q: "Can more than one spine condition occur together?",
        a: "Yes, it is common for more than one condition to be present. A specialist assessment identifies what is contributing to your symptoms.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["disc-problems", "spinal-stenosis", "back-pain"],
  },
  {
    id: 14,
    slug: "spine-friendly-sleeping-positions",
    title: "Spine-Friendly Sleeping Positions",
    category: "Spine Health",
    excerpt: "How you sleep can affect how your back and neck feel the next morning. Here is what tends to help.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Neat%20minimal%20bedroom%20with%20cosy%20neutral%20linen%20and%20soft%20morning%20light%2C%20calm%20restful%20atmosphere%2C%20clean%20interior%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-14&orientation=landscape&nocache=true",
    imageAlt: "Comfortable bedroom setting for spine-friendly sleep",
    metaTitle: "Best Sleeping Positions for Your Spine | Ortho Spine Care",
    metaDescription:
      "Find out which sleeping positions are gentler on the back and neck, and simple pillow tips. Patient-friendly guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-04-06",
    updatedDate: "2026-06-10",
    readTime: "4 min read",
    tags: ["Spine Health", "Sleep", "Neck Pain"],
    content: [
      {
        heading: "Why sleep position matters",
        paragraphs: [
          "A good mattress and sleeping position keep the spine in a neutral, supported line. When the neck or back is held at an awkward angle for hours, you may wake up stiff or sore.",
        ],
      },
      {
        heading: "Positions that are usually kinder to the spine",
        bullets: [
          "On the back with a pillow under the knees",
          "On the side with a pillow between the knees",
          "On the back with a pillow that supports the neck curve",
        ],
      },
      {
        heading: "Pillow and mattress tips",
        bullets: [
          "Choose a pillow that keeps the neck level with the spine",
          "Avoid sleeping on the stomach, which twists the neck",
          "Replace a mattress that has lost its support",
          "Use a supportive pillow rather than several stacked ones",
        ],
      },
    ],
    faqs: [
      {
        q: "Is sleeping on the stomach bad for the back?",
        a: "Sleeping on the stomach often forces the neck to turn to one side and can arch the lower back. Side or back positions are usually gentler.",
      },
      {
        q: "How firm should my mattress be?",
        a: "A mattress that supports the spine while allowing the shoulders and hips to sink slightly suits most people. What matters most is that it holds its shape.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain", "neck-pain"],
  },
  {
    id: 15,
    slug: "everyday-habits-that-can-affect-your-back",
    title: "Everyday Habits That Can Affect Your Back",
    category: "Back Pain",
    excerpt: "Back health is built in the small, repeated choices you make all day — not in one big decision.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Three%20dimensional%20anatomical%20render%20of%20the%20lumbar%20spine%20bearing%20load%20with%20the%20lower%20back%20region%20highlighted%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-15&orientation=landscape&nocache=true",
    imageAlt: "Person using safe lifting technique to protect the back",
    metaTitle: "Everyday Habits That Affect Your Back | Ortho Spine Care",
    metaDescription:
      "Simple everyday habits, from lifting technique to phone use, that affect back health. Practical patient guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-04-13",
    updatedDate: "2026-06-22",
    readTime: "5 min read",
    tags: ["Back Pain", "Prevention", "Posture"],
    content: [
      {
        heading: "Small habits, big effect",
        paragraphs: [
          "The back responds to repetition. The way you lift, sit, carry and even look at your phone, repeated hundreds of times, gradually shapes how your back feels.",
        ],
      },
      {
        heading: "Habits worth changing",
        bullets: [
          "Lifting with a rounded back instead of bending the knees",
          "Carrying a heavy bag on one shoulder only",
          "Cradling a phone with the neck bent for long periods",
          "Sitting slumped for hours without a break",
          "Ignoring early warning signs of strain",
        ],
      },
      {
        heading: "Habits worth building",
        bullets: [
          "Keep loads close to the body when lifting",
          "Switch shoulders or use a backpack for heavy bags",
          "Raise the phone rather than bending the neck",
          "Stand and move regularly",
          "Warm up before strenuous activity",
        ],
      },
    ],
    faqs: [
      {
        q: "Can one bad lift cause lasting back pain?",
        a: "A single awkward lift can trigger pain, but most such episodes settle. Repeating the same unsafe movements is what tends to build up problems over time.",
      },
      {
        q: "Does carrying a backpack cause back problems?",
        a: "A backpack worn evenly distributes weight better than a single-shoulder bag. Keep the load moderate and use both straps.",
      },
    ],
    relatedServices: ["spine-disorders"],
    relatedConditions: ["back-pain"],
  },
];